import { defineStore } from 'pinia';
import views from '@/constants/views';
import { hasAuthTokens } from '@/utilities/authTokens';
import { optimizeJobPhotoUrls } from '@/composables/useOptimizedPhotoUrl';
import { useFeatureFlagsStore } from '@/stores/useFeatureFlags';
import { featureFlagSlugs } from '@/constants/featureFlagSlugs';
import { useUserDetailsStore } from '@/stores/useUserDetails';

export const useJobsStore = defineStore('jobsStore', {
  state: () => {
    return {
      jobs: [],
      pagination: {
        totalElements: 0,
      },
      selectedJobs: [],
      stagedJobs: [],
      currentPageIndex: 0,
      rowsPerPage: 50,
      overdueJobsCount: 0,
      isPending: true,
      selectedView: views.CARD_VIEW.value,
      loadingJobId: null,
    };
  },
  actions: {
    _getJobDetailsUrl(id) {
      const featureFlagsStore = useFeatureFlagsStore();
      const isJobDetailsLoggingEnabled = featureFlagsStore.getByFlagName(
        featureFlagSlugs.jobDetailsLogging,
      );
      return isJobDetailsLoggingEnabled
        ? `/api/transport-requests/available/job-details/${id}`
        : `/api/transport-requests/${id}`;
    },
    async fetchJobDetails(id) {
      this.loadingJobId = id;
      try {
        const { $api } = useNuxtApp();
        const response = await $api(this._getJobDetailsUrl(id));
        const fullJob = optimizeJobPhotoUrls(response.data, '200x200');
        const index = this.jobs.findIndex((job) => job.id === id);
        if (index !== -1) {
          Object.assign(this.jobs[index], fullJob);
        }
      } catch {
        // fail silently — modal still shows with list data
      } finally {
        this.loadingJobId = null;
      }
    },
    clearFetchedJobs() {
      this.jobs = [];
      this.pagination = {};
      this.currentPageIndex = 0;
      this.rowsPerPage = 50;
    },
    clearSelectedJobs() {
      this.selectedJobs = [];
    },
    clearSingleSelectedJob(id) {
      this.selectedJobs = this.selectedJobs.filter((job) => job.id !== id);
    },
    /**
     * Stages the given jobs (defaults to the current selection). Accounts for currentJobCount
     * itself so every staging entry point gets that for free.
     * @param {Array} jobsToStage
     * @returns {Array} auctionIds for jobs that failed to stage
     * */
    async stageJobs(jobsToStage = this.selectedJobs) {
      const { $api } = useNuxtApp();
      const [bundleIds, transportIds] = jobsToStage.reduce(
        (acc, job) => {
          job.bundleId ? acc[0].push(job.id) : acc[1].push(job.id);

          return acc;
        },
        [[], []],
      );

      const body = { bundleIds, transportIds };

      const data = await $api('/api/transport-requests/stageJob', {
        method: 'POST',
        body: body,
      });

      const { results } = data.data;

      const stagedVehicleCount = jobsToStage
        .filter((job) => results[job.id])
        .reduce((total, job) => total + (job.vehicleCount || 1), 0);
      useUserDetailsStore().adjustCurrentJobCountForStagedVehicles(stagedVehicleCount);

      const auctionIdsByJobId = jobsToStage.reduce(
        (acc, job) => ({ ...acc, [job.id]: job.requestId }),
        {},
      );

      return Object.keys(results)
        .filter((id) => !results[id])
        .map((id) => auctionIdsByJobId[id]);
    },
    resetCurrentPageIndex() {
      this.currentPageIndex = 0;
    },
    async getCountOfOverdueJobs() {
      // Don't attempt to fetch if user is not authenticated
      if (!hasAuthTokens()) {
        return;
      }

      try {
        const { $api } = useNuxtApp();
        const data = await $api('/api/transport-requests/overdue/count');

        if (data?.data > 0) {
          this.overdueJobsCount = data.data;
        }
        return this.overdueJobsCount;
      } catch (error) {
        return;
      }
    },
    toggleSelectedView() {
      if (this.selectedView === views.CARD_VIEW.value) {
        this.selectedView = views.TABLE_VIEW.value;
      } else {
        this.selectedView = views.CARD_VIEW.value;
      }
      const { trackEvent, eventNames } = useAcvAnalytics();
      trackEvent(eventNames.MANAGE_LIST_VIEW_TOGGLED, {
        selectedView: this.selectedView,
      });
    },
  },
  getters: {
    jobIds: (state) => state.selectedJobs.map((job) => job.id),
    selectedJobsVehicleCount: (state) =>
      state.selectedJobs.reduce((total, job) => total + (job.vehicleCount || 1), 0),
  },
});
